import React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import type { UserAction } from '@stores/user/action';
import type { AppStore } from '@stores/app/store';
import { syncModalBodyScrollLock, releaseModalBodyScrollLock } from '@utils/modalScrollLock';
import H5 from './layout/h5';
import type { VCoinRecordLayoutProps } from './types';
import styles from './index.module.scss';

// V 币任务页 App deeplink，用于唤起 vivo 社区 App。
const V_COIN_MALL_APP_URL = 'vivoclub://com.vivo.club/deep?page=coins_collection';

interface Props extends RouteComponentProps {
    user?: UserAction;
    app?: AppStore;
}

interface State {
    records: VCoinRecordLayoutProps['records'];
    loading: boolean;
    hasMore: boolean;
    error: string;
    needsLogin: boolean;
    earnTipVisible: boolean;
    weChatBrowserGuideVisible: boolean;
}

@inject('user', 'app')
@observer
class VCoinRecord extends React.Component<Props, State> {
    state: State = {
        records: [],
        loading: false,
        hasMore: true,
        error: '',
        needsLogin: false,
        earnTipVisible: false,
        weChatBrowserGuideVisible: false,
    };

    private page = 1;
    private readonly pageSize = 10;
    private restoreRootFontSize?: () => void;

    private syncH5RemOnDesktop = () => {
        if (typeof window === 'undefined' || this.props.app?.platform !== 'pc') return;

        const root = document.documentElement;
        if (root.style.fontSize === '100px') return;
        const previousFontSize = root.style.fontSize;
        root.style.fontSize = '100px';
        this.restoreRootFontSize = () => {
            if (root.style.fontSize === '100px') {
                root.style.fontSize = previousFontSize;
            }
        };
    };

    componentDidMount() {
        document.title = 'V币明细';
        this.syncH5RemOnDesktop();

        // 页面初始化强制校验登录状态，未登录时由 checkLogin 负责跳转登录页。
        if (!this.ensureLogin()) return;

        this.loadRecords();
        window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.onScroll);
        releaseModalBodyScrollLock(this.state.earnTipVisible);
        this.restoreRootFontSize?.();
    }

    componentDidUpdate(_prevProps: Props, prevState: State) {
        this.syncH5RemOnDesktop();
        // 弹框打开时锁定页面滚动，关闭时恢复页面滚动。
        syncModalBodyScrollLock(this.state.earnTipVisible, prevState.earnTipVisible);
    }

    // 分页加载 V 币明细；余额统一从用户信息中的 score 字段读取。
    loadRecords = async () => {
        const { user } = this.props;
        const { loading, hasMore } = this.state;
        if (!user || loading || !hasMore) return;

        this.setState({ loading: true, error: '', needsLogin: false });
        try {
            const res: any = await user.getUserScoreRecords({
                page: this.page,
                perPage: this.pageSize,
            });
            if (res.code !== 0) {
                this.setState({
                    loading: false,
                    error: res.msg || 'V币明细加载失败',
                    needsLogin: Number(res.code) === -3001,
                });
                return;
            }
            const data = res.data || {};
            const newRecords = Array.isArray(data) ? data : (data.list || data.pageData || data.data || []);
            this.setState(prev => ({
                records: this.page === 1 ? newRecords : [...prev.records, ...newRecords],
                loading: false,
                hasMore: newRecords.length >= this.pageSize,
                needsLogin: false,
            }));
        } catch (error) {
            this.setState({ loading: false, error: '网络开小差了，请稍后重试', needsLogin: false });
        }
    };

    // 登录态统一交给 UserAction 判断。access_token 可能是 HttpOnly Cookie，
    // 前端读不到并不代表未登录，不能在这里主动 logout，否则 OAuth 返回后会再次跳转形成循环。
    ensureLogin = () => {
        const { user, app } = this.props;
        const platform = app?.platform === 'h5' ? 'h5' : 'pc';
        if (!user?.checkLogin) return true;
        return user.checkLogin(platform);
    };

    // 接口明确返回未登录时，只在用户主动点击重试后重新发起登录，
    // 避免页面加载与 OAuth 回跳互相触发导致自动刷新循环。
    retryRecords = () => {
        if (this.state.needsLogin) {
            this.props.user?.logout?.();
            this.ensureLogin();
            return;
        }

        this.page = 1;
        this.setState({ hasMore: true }, this.loadRecords);
    };

    // 页面接近底部时自动请求下一页记录。
    onScroll = () => {
        const distance = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
        if (distance < 160 && !this.state.loading && this.state.hasMore) {
            this.page += 1;
            this.loadRecords();
        }
    };

    // 返回上一页；没有历史记录时回到首页。
    goBack = () => {
        if (this.props.history.length > 1) this.props.history.goBack();
        else this.props.history.push('/');
    };

    // 关闭赚取 V 币提示弹框。
    hideEarnTip = () => {
        this.setState({ earnTipVisible: false });
    };

    // PC 端点击赚取入口直接进入下载 App 页面。
    goDownloadApp = () => {
        this.props.history.push('/downloadAppEnter');
    };

    // 关闭弹框并尝试唤起 App，未安装时 3 秒后跳转下载页。
    openAppMall = async () => {
        // 微信内置浏览器会拦截 App deeplink，改为顶部悬浮引导。
        if (/micromessenger/i.test(navigator.userAgent)) {
            this.setState({
                earnTipVisible: false,
                weChatBrowserGuideVisible: true,
            });
            return;
        }

        this.hideEarnTip();

        try {
            // 复制 deeplink，便于浏览器限制唤起时用户手动打开。
            await navigator.clipboard?.writeText?.(V_COIN_MALL_APP_URL);
        } catch (_) {
            // 剪切板不可用时仍继续尝试唤起 App。
        }

        window.location.href = V_COIN_MALL_APP_URL;
        window.setTimeout(() => {
            this.props.history.push(`/downloadAppEnter?deepLink=${encodeURIComponent(V_COIN_MALL_APP_URL)}`);
        }, 3000);
    };

    renderLayoutProps = (): VCoinRecordLayoutProps => ({
        records: this.state.records,
        score: this.props.user?.userInfo?.score ?? 0,
        loading: this.state.loading,
        hasMore: this.state.hasMore,
        error: this.state.error,
        earnTipVisible: this.state.earnTipVisible,
        weChatBrowserGuideVisible: this.state.weChatBrowserGuideVisible,
        onBack: this.goBack,
        onOpenEarnTip: () => this.setState({ earnTipVisible: true }),
        onGoDownloadApp: this.goDownloadApp,
        onHideEarnTip: this.hideEarnTip,
        onHideWeChatBrowserGuide: () => this.setState({ weChatBrowserGuideVisible: false }),
        onRetry: this.retryRecords,
        onOpenAppMall: this.openAppMall,
    });

    render() {
        const { app } = this.props;
        const layoutProps = this.renderLayoutProps();
        const platform = app?.platform;

        // App 平台信息尚未初始化时，暂不选择具体页面布局。
        if (!platform) {
            return (
                <div className={styles.loading}>
                    <div>加载中...</div>
                </div>
            );
        }

        // PC 保留站点外壳，V 币明细正文统一使用 H5 页面。
        return (
            <div className={styles.h5Wrap}>
                <H5 {...layoutProps} />
            </div>
        );
    }
}

export default withRouter(VCoinRecord);
